import React, { createContext, useContext, useEffect, useMemo } from 'react'

import { SecurityConfig } from '../types'
import { productionHades, stagingHades } from '../config'

interface Props {
  children: React.ReactNode | React.ReactNodeArray
  value?: SecurityConfig
}

const defaultRedirectUri = `${window.location.origin}${window.location.pathname}`

const defaultSecurityConfig: SecurityConfig = {
  redirectUri: defaultRedirectUri,
  loginPath: '/login',
  successPath: '/',
  twoFactorAuthPath: '/2fa',
  phoneVerificationPath : '/verifyphone',
  emailVerificationPath : '/verifyemail',
  loading: 'Loading...',
  env: 'production',
}

export const SecurityConfigContext = createContext<SecurityConfig>(
  defaultSecurityConfig
)

export const SecurityConfigProvider: React.FC<Props> = ({
  children,
  value = {},
}) => {
  const serverUrl = useMemo(() => {
    if (value.endpoint) return value.endpoint

    if (value.env === 'development') {
      throw new Error(
        'You need to pass the `endpoint` when the environment is `development`.'
      )
    }

    return value.env === 'production' ? productionHades : stagingHades
  }, [value])

  useEffect(() => {
    window.hades = {
      serverUrl,
      loginPath: value.loginPath,
    }
  }, [value])

  return (
    <SecurityConfigContext.Provider
      value={{
        ...defaultSecurityConfig,
        ...value,
      }}
    >
      {children}
    </SecurityConfigContext.Provider>
  )
}

export const useSecurityConfig = () => {
  const context = useContext(SecurityConfigContext)

  if (!context) {
    throw new Error(
      'useSecurityConfigContext must be used within the securityProvider'
    )
  }

  return context
}
